<template>
  <Copy element="div" :class="`${el}__accountDataField`">
    <Modal
      :open="isOpen"
      :headline="
        deleteRequested
          ? $t('profile.accountData.modal.delete.headline')
          : $t('profile.accountData.modal.headline')
      "
      size="s"
      @onTrackingClick="trackModalClose"
    >
      <Copy
        v-if="statusCode === 403 || statusCode === 400"
        size="m"
        color="supportDanger"
        centered
        bold
        responsive
        :staticClass="`${el}__personalDataFormError`"
      >
        {{
          statusCode === 403
            ? $t('profile.access.form.password.authentication.failed')
            : $t('profile.forms.error.generics')
        }}
      </Copy>

      <form @submit.prevent="handleSubmit">
        <Copy v-if="deleteRequested">
          {{ $t('profile.accountData.modal.delete.text')[0] }}
          <strong>{{ $t('profile.accountData.modal.delete.text')[1] }}</strong>
        </Copy>
        <Copy v-else>
          {{ $t('profile.accountData.modal.text') }}
        </Copy>

        <div class="u-margin__top--4">
          <BaseInput
            :label="$t('profile.access.form.password.label')"
            type="password"
            name="password"
            v-model="formFields.password.value"
            :error="formFields.password.error"
            @input="clearError(formFields.password)"
            @iconClicked="
              () =>
                $emit('onTrackingClick', '-Löschen-MD-Pop-up_click-Passwort anzeigen', 'element')
            "
            clearable
          />
        </div>

        <Row class="u-margin__top--4">
          <Col :xs="12" center>
            <Col :xs="4"></Col>
          </Col>
        </Row>

        <div :class="`${el}__buttonRow ${el}__buttonRow--centered`">
          <Button
            :label="$t('profile.button.label.cancel')"
            type="outlined"
            @click="trackModalClose('cancelButton')"
            small
          />
          <Button
            :label="
              deleteRequested
                ? $t('profile.accountData.modal.delete.button.confirm')
                : $t('profile.accountData.downloadData.link')
            "
            type="primary"
            :loading="isLoading"
            submit
            class="u-margin__left--2"
            small
          />
        </div>
      </form>
    </Modal>
    <Row marginBottom>
      <Col :xs="4" :l="3">{{ $t('profile.accountData.downloadData.label') }}</Col>
      <Col :xs="8" :l="9">
        {{ $t('profile.accountData.downloadData.text') }}
        <Link @click="openModal">
          {{ $t('profile.accountData.downloadData.link') }}
        </Link>
      </Col>
    </Row>

    <Row class="u-margin__bottom--3">
      <Col :xs="4" :l="3">{{ $t('profile.accountData.deleteData.label') }}</Col>
      <Col :xs="8" :l="9">
        <Link @click="openDeleteModal" :class="`${el}__deleteLink`">
          {{ $t('profile.accountData.deleteData.confirm') }}
        </Link>
      </Col>
    </Row>
  </Copy>
</template>

<script>
import { mapFieldsHealth } from '@/store/mapFields';
import Row from '@/components/Grid/Row';
import Col from '@/components/Grid/Col';
import Link from '@/objects/Link';
import BaseInput from '@/objects/Forms/BaseInput';
import Copy from '@/objects/Copy/Copy';
import Modal from '@/components/Modal';
import Button from '@/objects/Button';

import { validateForm } from '@/helpers/formValidation';

import {
  ROUTE_API_CHECK_PASSWORD,
  ROUTE_API_DELETE_HEALTH,
  ROUTE_API_DELETE_PROFILE,
  ROUTE_API_DELETE_USER,
  ROUTE_API_GET_DISCLOSURE_DOWNLOAD,
  ROUTE_API_GET_USER,
} from '@/constants/apiRoutes';
import { ROUTE_LOGIN } from '@/constants/routes';
import { AUTH_LOGOUT } from '@/store/actions/auth';

import Vue from 'vue';
import { createValidationData } from '@/helpers/tracking/validationTracking';

export default {
  name: 'AccountData',
  components: { BaseInput, Button, Modal, Copy, Link, Col, Row },
  props: {
    el: String,
  },
  computed: {
    ...mapFieldsHealth(['healthUuid']),
  },
  data() {
    return {
      isOpen: false,
      statusCode: null,
      isLoading: false,
      deleteRequested: false,
      formFields: {
        password: {
          value: '',
          error: null,
          required: this.$t('profile.access.form.error.password.required'),
        },
      },
    };
  },
  methods: {
    handleSubmit(e) {
      this.isLoading = true;

      const { isValid } = validateForm(e.target.elements, this.formFields);

      const passwordObject = { password: this.formFields.password.value };

      if (isValid) {
        Vue.axios
          .post(ROUTE_API_CHECK_PASSWORD, passwordObject)
          .then((response) => {
            if (response.data.success) {
              if (this.deleteRequested) {
                this.deleteAccount(passwordObject);
              } else {
                this.getAccountData();
              }
            }
          })
          .catch((error) => {
            /*
            422 = validation error
            401 = unauthorized (cookie problem) or user blocked
            403 = wrong pw
            400 = something went wrong in dpa-api
             */

            this.statusCode = error.response.status;
            if (this.statusCode === 401) {
              if (this.deleteRequested) {
                this.logout();
              }
            }
            if (this.statusCode === 422) {
              this.formFields['password'].error = this.$t(
                'profile.access.form.error.password.required'
              );
            }
            this.isLoading = false;
          });
      } else {
        createValidationData(this.formFields);
        this.isLoading = false;
      }
    },

    deleteAccount(data) {
      Promise.all([
        Vue.axios.post(ROUTE_API_DELETE_HEALTH),
        Vue.axios.delete(ROUTE_API_DELETE_PROFILE),
        Vue.axios.post(ROUTE_API_DELETE_USER, data),
      ])
        .then(() => {
          this.trackModalClose('submitButton');
          this.logout();
        })
        .catch((error) => {
          this.statusCode = error.response.status;
          this.logout();
        });
    },

    getAccountData() {
      Vue.axios.get(ROUTE_API_GET_USER).then((response) => {
        const user = response.data;
        Vue.axios
          .post(ROUTE_API_GET_DISCLOSURE_DOWNLOAD, {
            personInNeedOfCareUuid: this.healthUuid,
            user,
          })
          .then((response) => {
            this.download(response.data);
            this.closeModal();
            this.isLoading = false;
          })
          .catch((error) => {
            this.statusCode = error.response.status;
            this.isLoading = false;
          });
      });
    },

    download(content) {
      let filename = 'Profildaten.txt';
      let link = document.createElement('a');
      const blob = new Blob([content], { type: 'application/txt' });
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', filename);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.trackModalClose('submitButton');
    },

    openDeleteModal() {
      this.deleteRequested = true;
      this.openModal();
    },
    openModal() {
      this.isOpen = true;
      this.$emit(
        'onTrackingClick',
        this.deleteRequested
          ? '-Löschen-MD_click-' + this.$t('profile.accountData.deleteData.confirm')
          : '-MD_click-' + this.$t('profile.accountData.downloadData.link'),
        'element'
      );
    },
    closeModal() {
      this.isOpen = false;
      this.statusCode = '';
      this.formFields.password.value = '';
      this.clearError(this.formFields.password);
      this.deleteRequested = false;
    },

    logout() {
      this.closeModal();
      this.$store.dispatch(AUTH_LOGOUT).catch(() => {
        this.$router.push(ROUTE_LOGIN + '?account=deleted');
      });
    },

    clearError(field) {
      field.error = null;
    },

    trackModalClose(src) {
      let label = (this.deleteRequested ? '-Löschen-MD' : '-MD-herunterladen') + '-Pop-up_';
      let element = 'element';

      switch (src) {
        case 'icon':
          label = label + 'click-X';
          break;
        case 'background':
          label = label + 'click-background';
          break;
        case 'cancelButton':
          label = label + 'btn-' + this.$t('profile.button.label.cancel');
          element = 'button';
          break;
        case 'submitButton':
          label =
            label +
            'btn-' +
            this.$t(
              this.deleteRequested
                ? 'profile.accountData.modal.delete.button.confirm'
                : 'profile.accountData.downloadData.link'
            );
          element = 'button';
          break;
      }

      this.$emit('onTrackingClick', label, element);

      this.closeModal();
    },
  },
};
</script>
